<template>
  <div class="tdot-calendar-dialog">
    <v-dialog v-model="open" max-width="860" :fullscreen="$vuetify.breakpoint.xsOnly" scrollable content-class="tdot-participants-dialog">
      <v-card :dark="isDarkMode" class="tdot-list" :class="{ 'tdot-list--dark': isDarkMode }">
        <div class="tdot-list__hero">
          <div class="tdot-list__hero-top">
            <span class="tdot-list__eyebrow">31. Oktober &amp; 1. November 2026</span>
            <v-btn icon dark aria-label="Teilnehmerliste schliessen" @click="open = false"><v-icon>mdi-close</v-icon></v-btn>
          </div>
          <div class="tdot-list__heading">
            <div>
              <div class="tdot-list__event-label">TAG DER OFFENEN TÜR</div>
              <h2>Teilnahmen &amp; Termine</h2>
            </div>
            <div class="tdot-list__total" aria-live="polite"><strong>{{ total === null ? '–' : total }}</strong><span>Teilnahmen</span></div>
          </div>
          <div class="tdot-list__prize">
            <v-icon size="18" color="#edd8a5">mdi-gift-outline</v-icon>
            <span>1 Woche Alibey Sportwoche <span class="tdot-list__prize-detail">· all inclusive · exkl. Flug</span></span>
          </div>
        </div>
        <v-card-text class="tdot-list__body">
          <div class="tdot-list__actions">
            <v-btn depressed color="#245b44" dark class="tdot-list__capture" @click="capture"><v-icon left size="19">mdi-account-plus-outline</v-icon>Teilnahme erfassen</v-btn>
            <div class="tdot-list__tools">
              <v-btn text small :loading="exporting" class="tdot-list__export" @click="exportEntries"><v-icon left small>mdi-download</v-icon>Export</v-btn>
              <v-btn icon :loading="loading" aria-label="Teilnehmerliste aktualisieren" @click="load"><v-icon size="21">mdi-refresh</v-icon></v-btn>
            </div>
          </div>
          <v-text-field v-model="search" outlined dense clearable hide-details label="Person suchen" placeholder="Name, E-Mail oder Telefon"
            prepend-inner-icon="mdi-magnify" color="#438466" class="tdot-list__search" />
          <v-chip-group v-model="membership" mandatory active-class="tdot-list__filter--active" class="tdot-list__filters">
            <v-chip value="all" filter outlined>Alle</v-chip>
            <v-chip value="member" filter outlined>Mitglieder</v-chip>
            <v-chip value="guest" filter outlined>Neue Gäste</v-chip>
          </v-chip-group>
          <div class="tdot-list__result-info"><strong>{{ filtered }} {{ filtered === 1 ? 'Person' : 'Personen' }}</strong><span>Beide Tage · Mitgliedschaft laut Eigenangabe</span></div>
          <v-alert v-if="error" type="error" text dense>{{ error }}</v-alert>
          <v-progress-linear :active="loading" :indeterminate="loading" height="2" color="#438466" />
          <div v-if="!loading && !entries.length && !error" class="tdot-list__empty" role="status">
            <span class="tdot-list__empty-icon"><v-icon size="30" :color="isDarkMode ? '#b7d2bf' : '#52745f'">{{ isFiltered ? 'mdi-account-search-outline' : 'mdi-gift-outline' }}</v-icon></span>
            <h3>{{ isFiltered ? 'Keine passende Person gefunden.' : 'Der Lostopf wartet.' }}</h3>
            <p>{{ isFiltered ? 'Versuche einen anderen Suchbegriff oder passe den Filter an.' : 'Neue Teilnahmen erscheinen hier. Wähle anschliessend eine Person aus, um einen Termin zu vereinbaren.' }}</p>
          </div>
          <div v-for="entry in entries" :key="entry._id" class="tdot-person">
            <button type="button" class="tdot-person__main" @click="select(entry)">
              <span class="tdot-person__avatar">{{ initials(entry) }}</span>
              <span class="tdot-person__copy">
                <span class="tdot-person__heading">
                  <strong>{{ entry.name }} {{ entry.surname }}</strong>
                  <span class="tdot-person__membership" :class="{ 'tdot-person__membership--guest': entry.membership !== 'member' }">{{ entry.membership === 'member' ? 'Mitglied' : 'Neuer Gast' }}</span>
                </span>
                <span class="tdot-person__contact">{{ entry.email }} · {{ entry.phone }}</span>
                <span class="tdot-person__tags">
                  <span><v-icon size="13">mdi-phone-outline</v-icon>{{ entry.consent.contact ? 'Kontakt erlaubt' : 'Keine Kontaktfreigabe' }}</span>
                  <span><v-icon size="13">mdi-email-outline</v-icon>{{ newsletterLabel(entry) }}</span>
                </span>
                <span v-if="entry.appointment" class="tdot-person__appointment"><v-icon size="14" color="inherit">mdi-calendar-check-outline</v-icon>Termin · {{ $moment(entry.appointment.date).format('DD.MM.YYYY') }} · {{ entry.appointment.start }}</span>
              </span>
              <v-icon size="20" class="tdot-person__arrow">mdi-chevron-right</v-icon>
            </button>
            <div v-if="!entry.leadReady || entry.newsletterStatus === 'error'" class="tdot-person__retry">
              <span>{{ !entry.leadReady ? 'Lead-Speicherung noch offen' : 'Newsletter-Anmeldung fehlgeschlagen' }}</span>
              <v-btn small text color="teal" :loading="retrying === entry._id" @click="retry(entry)">Erneut versuchen</v-btn>
            </div>
          </div>
          <v-pagination v-if="pages > 1" v-model="page" :length="pages" :total-visible="5" color="#245b44" class="mt-4" />
          <div v-if="entries.length" class="tdot-list__hint"><v-icon size="15">mdi-calendar-plus</v-icon>Person auswählen und Termin planen.</div>
        </v-card-text>
      </v-card>
    </v-dialog>
    <work-calendar-guestbook-dialog v-model="detailOpen" :entry="activeEntry" :is-dark-mode="isDarkMode"
      source-label="Tag der offenen Tür · Wettbewerb" :assigned-employee-id="assignedEmployeeId"
      :assigned-employee-name="assignedEmployeeName" :confirm-appointment-creation="confirmAppointmentCreation"
      @refresh="load" @resolved="resolved" />
  </div>
</template>
<script>
import axios from "axios"
import { saveAs } from "file-saver"
import WorkCalendarGuestbookDialog from "./WorkCalendarGuestbookDialog.vue"
const options = () => ({ headers: { Authorization: localStorage.getItem("token") } })
export default {
  name: "WorkCalendarTdot",
  components: { WorkCalendarGuestbookDialog },
  props: {
    value: Boolean, studioId: { type: String, required: true }, isDarkMode: Boolean,
    assignedEmployeeId: { type: String, default: "" }, assignedEmployeeName: { type: String, default: "" },
    confirmAppointmentCreation: { type: Function, default: () => true },
  },
  data: () => ({ entries: [], total: null, filtered: 0, page: 1, pages: 0,
    search: "", membership: "all", loading: false, error: "", detailOpen: false, activeEntry: null,
    exporting: false, retrying: "", sequence: 0, timer: null }),
  computed: {
    open: { get() { return this.value }, set(value) { this.$emit("input", value) } },
    isFiltered() { return !!(this.search || "").trim() || this.membership !== "all" },
  },
  watch: {
    open(value) { if (value) this.load() },
    search() { this.page = 1; this.refresh() }, membership() { this.page = 1; this.refresh() },
    page() { this.load() },
  },
  mounted() { this.$emit("count", null); this.load() },
  beforeDestroy() { this.sequence++; clearTimeout(this.timer) },
  methods: {
    refresh() { clearTimeout(this.timer); this.timer = setTimeout(() => this.load(), 250) },
    async load() {
      const sequence = ++this.sequence
      this.loading = true; this.error = ""
      try {
        const endpoint = this.open ? "entries" : "count"
        const { data } = await axios.get(`/api/tdot/staff/${endpoint}`, { ...options(), params: { search: this.search || "", membership: this.membership, page: this.page } })
        if (sequence !== this.sequence || data.studioId !== this.studioId) return
        this.total = data.total
        this.$emit("count", this.total)
        if (endpoint === "entries") { this.entries = data.entries; this.filtered = data.filtered; this.pages = data.pages }
      } catch (error) {
        if (sequence === this.sequence) this.error = error.response?.data?.message || "Teilnahmen konnten nicht geladen werden."
      } finally { if (sequence === this.sequence) this.loading = false }
    },
    initials(entry) { return `${entry.name.charAt(0)}${entry.surname.charAt(0)}` },
    newsletterLabel(entry) {
      return ({ subscribed: "Newsletter abonniert", pending: "Newsletter: Bestätigung offen", error: "Newsletter: Fehler",
        requested: "Newsletter angefragt", not_requested: "Newsletter nicht angefragt", not_subscribed: "Kein Newsletter", unsubscribed: "Newsletter abgemeldet", unknown: "Newsletterstatus unbekannt", cleaned: "Newsletter: Adresse prüfen" })[entry.newsletterStatus] || "Newsletterstatus unbekannt"
    },
    select(entry) {
      if (entry.appointment) { this.open = false; this.$emit("resolved", { appointment: entry.appointment, withoutAccount: true }); return }
      this.activeEntry = entry; this.detailOpen = true
    },
    resolved(result) { this.detailOpen = false; this.open = false; this.$nextTick(() => this.load()); this.$emit("resolved", result) },
    capture() { this.$router.push({ name: "guestbookEntry", query: { mode: "tdot" } }) },
    async exportEntries() {
      this.exporting = true
      try { const result = await axios.get("/api/tdot/staff/export", { ...options(), responseType: "blob" }); saveAs(result.data, "TDOT-2026.csv") }
      catch (_) { this.error = "Der Export konnte nicht geladen werden." }
      finally { this.exporting = false }
    },
    async retry(entry) {
      this.retrying = entry._id
      try { await axios.post(`/api/tdot/staff/entries/${entry._id}/retry`, {}, options()); await this.load() }
      catch (_) { this.error = "Bitte versuche die offene Speicherung später erneut." }
      finally { this.retrying = "" }
    },
  },
}
</script>
<style scoped>
.tdot-list { --tdot-ink: #24372c; --tdot-muted: #69766e; --tdot-border: #dfe5df; --tdot-panel: #fff; --tdot-soft: #f0f4ee; border-radius: 22px; background: #f8f9f5; color: var(--tdot-ink); }
.tdot-list--dark { --tdot-ink: #e7eee8; --tdot-muted: #a8b8af; --tdot-border: #35473d; --tdot-panel: #243229; --tdot-soft: #2c3d32; background: #1b2720; }
.tdot-list__hero { flex: 0 0 auto; padding: 18px 28px 22px; background: radial-gradient(ellipse at top right, #416c4f 0, transparent 65%), #183c2d; color: #fcfbf1; border-radius: 22px 22px 0 0; }
.tdot-list__hero-top { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.tdot-list__eyebrow { color: #d6ddc9; font-size: 11px; font-weight: 600; letter-spacing: 1.2px; }
.tdot-list__hero-top .v-btn { margin-right: -10px; }
.tdot-list__heading { display: flex; align-items: center; justify-content: space-between; gap: 22px; margin-top: 7px; }
.tdot-list__event-label { margin-bottom: 7px; color: #edd8a5; font-size: 10px; font-weight: 700; letter-spacing: 1.8px; }
.tdot-list__heading h2 { margin: 0; color: inherit; font-size: 28px; font-weight: 650; line-height: 1.2; letter-spacing: -.6px; }
.tdot-list__total { display: flex; flex-direction: column; align-items: center; padding-left: 24px; border-left: 1px solid #ffffff2b; }
.tdot-list__total strong { font-size: 33px; line-height: 1.1; font-weight: 600; font-variant-numeric: tabular-nums; }
.tdot-list__total span { margin-top: 5px; font-size: 11px; color: #d6ddc9; }
.tdot-list__prize { display: flex; align-items: center; gap: 9px; margin-top: 20px; padding-top: 14px; border-top: 1px solid #ffffff1f; color: #f0e5c7; font-size: 12px; line-height: 1.6; }
.tdot-list__prize-detail { color: #c4d1c3; white-space: nowrap; }
.tdot-list .tdot-list__body { padding: 22px 28px 24px; color: var(--tdot-ink); }
.tdot-list__actions { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 20px; }
.tdot-list__tools { display: flex; align-items: center; gap: 4px; }
.tdot-list__capture.v-btn { height: 40px; border-radius: 10px; text-transform: none; letter-spacing: .1px; font-weight: 600; }
.tdot-list__export.v-btn { text-transform: none; letter-spacing: 0; color: var(--tdot-muted); }
.tdot-list__search { border-radius: 11px; background: var(--tdot-panel); }
.tdot-list__filters { margin-top: 12px; }
.tdot-list__filters .v-chip { height: 30px; border-color: var(--tdot-border); font-size: 12px; color: var(--tdot-muted); }
.tdot-list__filters .tdot-list__filter--active { background: var(--tdot-soft); color: var(--tdot-ink); border-color: #7d9e87; font-weight: 600; }
.tdot-list__result-info { display: flex; justify-content: space-between; flex-wrap: wrap; gap: 5px 16px; margin: 10px 0 12px; font-size: 11px; line-height: 1.6; color: var(--tdot-muted); }
.tdot-list__result-info strong { color: var(--tdot-ink); font-weight: 600; }
.tdot-list__empty { padding: 34px 20px; text-align: center; border: 1px dashed var(--tdot-border); border-radius: 14px; background: var(--tdot-panel); }
.tdot-list__empty-icon { display: inline-grid; place-items: center; width: 60px; height: 60px; margin-bottom: 16px; background: var(--tdot-soft); border-radius: 20px; }
.tdot-list__empty h3 { color: var(--tdot-ink); font-size: 17px; font-weight: 600; }
.tdot-list__empty p { max-width: 370px; margin: 8px auto 0; color: var(--tdot-muted); font-size: 13px; line-height: 1.65; }
.tdot-person { border: 1px solid var(--tdot-border); border-radius: 12px; margin-top: 8px; background: var(--tdot-panel); overflow: hidden; }
.tdot-person__main { width: 100%; text-align: left; color: inherit; display: flex; align-items: center; gap: 13px; padding: 15px; }
.tdot-person__main:hover, .tdot-person__main:focus-visible { background: var(--tdot-soft); outline: 2px solid #438466; outline-offset: -2px; border-radius: 12px; }
.tdot-person__avatar { display: grid; place-items: center; width: 42px; height: 44px; flex-shrink: 0; background: var(--tdot-soft); color: var(--tdot-ink); border-radius: 12px; font-weight: 600; font-size: 14px; }
.tdot-person__copy { display: flex; flex-direction: column; gap: 4px; min-width: 0; flex: 1; overflow-wrap: anywhere; }
.tdot-person__heading { display: flex; align-items: center; flex-wrap: wrap; gap: 5px 10px; }
.tdot-person__heading strong { font-size: 14px; font-weight: 650; }
.tdot-person__membership { padding: 1px 7px; border-radius: 5px; background: var(--tdot-soft); color: var(--tdot-ink); font-size: 10px; line-height: 1.8; }
.tdot-person__membership--guest { border: 1px solid var(--tdot-border); background: transparent; }
.tdot-person__contact { font-size: 12px; color: var(--tdot-muted); }
.tdot-person__tags { display: flex; flex-wrap: wrap; gap: 4px 14px; margin-top: 4px; color: var(--tdot-muted); font-size: 11px; }
.tdot-person__tags > span, .tdot-person__appointment { display: inline-flex; align-items: center; gap: 5px; }
.tdot-person__tags .v-icon { color: inherit; }
.tdot-person__appointment { margin-top: 5px; font-size: 12px; color: #367855; font-weight: 600; }
.tdot-list--dark .tdot-person__appointment { color: #a1d3b2; }
.tdot-person__arrow { color: var(--tdot-muted); }
.tdot-person__retry { display: flex; align-items: center; flex-wrap: wrap; padding: 0 15px 10px; font-size: 12px; color: var(--tdot-muted); }
.tdot-list__hint { display: flex; align-items: center; justify-content: center; gap: 7px; margin-top: 18px; color: var(--tdot-muted); font-size: 11px; }
.tdot-list__hint .v-icon { color: inherit; }
@media (max-width: 599px) {
  .tdot-list, .tdot-list__hero { border-radius: 0; }
  .tdot-list__hero { padding: 14px 20px 18px; }
  .tdot-list .tdot-list__body { padding: 20px 16px; }
  .tdot-list__heading { gap: 14px; }
  .tdot-list__heading h2 { font-size: 22px; }
  .tdot-list__total { padding-left: 14px; }
  .tdot-list__total strong { font-size: 28px; }
  .tdot-list__eyebrow { font-size: 10px; letter-spacing: .5px; }
  .tdot-list__prize-detail { display: block; }
  .tdot-list__actions { flex-wrap: wrap; gap: 6px; }
  .tdot-list__tools { margin-left: auto; }
  .tdot-person__main { gap: 10px; padding: 13px 11px; }
  .tdot-person__avatar { width: 34px; height: 36px; font-size: 12px; border-radius: 10px; }
  .tdot-person__arrow { display: none; }
}
</style>
